/**
 * Canvas.jsx — React Flow visual workflow builder
 *
 * Renders:
 *  • One TriggerNode (always first, not deletable)
 *  • StepNodes for each step in the workflow
 *  • Animated edges connecting them in order
 *
 * Emits:
 *  onSelectNode(id | null)   — node clicked
 *  onDeleteNode(id)          — node delete button
 *  onNodesChange(nodes)      — position updates after drag
 */

import {
  ReactFlow, Background, Controls, MiniMap,
  Handle, Position, NodeToolbar, BackgroundVariant,
  useNodesState, useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useEffect, useCallback } from 'react';
import {
  Zap, Globe, Code2, Filter, MessageSquare, Mail,
  Trash2, Clock, Play,
} from 'lucide-react';
import { STEP_TYPES } from '../constants.js';

// ── Icon map ─────────────────────────────────────────────────────────────────
const ICONS = { Zap, Globe, Code2, Filter, MessageSquare, Mail, Clock, Play };

function StepIcon({ name, size = 14 }) {
  const Icon = ICONS[name] ?? Zap;
  return <Icon size={size} />;
}

// ── Trigger Node ─────────────────────────────────────────────────────────────
function TriggerNode({ data, selected }) {
  const TRIGGER_ICONS = { webhook: 'Globe', cron: 'Clock', manual: 'Play' };
  const iconName = TRIGGER_ICONS[data.triggerType] ?? 'Zap';

  return (
    <div
      className={`wf-node ${selected ? 'selected' : ''}`}
      style={{ '--node-color': 'var(--primary)' }}
    >
      <div className="wf-node-header">
        <div className="wf-node-icon">
          <StepIcon name={iconName} size={14} />
        </div>
        <div>
          <div className="wf-node-title">{data.label || 'Trigger'}</div>
        </div>
      </div>
      <div className="wf-node-body">
        {data.triggerType ?? 'webhook'}
        {data.triggerType === 'cron' && data.cronExpr && ` · ${data.cronExpr}`}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

// ── Step Node ─────────────────────────────────────────────────────────────────
function StepNode({ data, selected }) {
  const meta = STEP_TYPES[data.type] ?? {};

  return (
    <>
      <NodeToolbar isVisible={selected} position={Position.Right} offset={8}>
        <button
          onClick={() => data.onDelete?.(data.stepId)}
          style={{
            background: 'var(--surface)',
            border: '1px solid rgba(239,68,68,.4)',
            borderRadius: 7,
            color: 'var(--red)',
            padding: '5px 8px',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
            fontSize: '.75rem',
          }}
        >
          <Trash2 size={12} /> Remove
        </button>
      </NodeToolbar>

      <div
        className={`wf-node ${selected ? 'selected' : ''}`}
        style={{ '--node-color': meta.color ?? '#818CF8' }}
      >
        <Handle type="target" position={Position.Top} />

        <div className="wf-node-header">
          <div className="wf-node-icon">
            <StepIcon name={meta.icon ?? 'Zap'} size={14} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="wf-node-title">{data.name || meta.label || data.type}</div>
          </div>
        </div>

        <div className="wf-node-body">
          {data.type === 'http_request' && data.config?.url
            ? `${data.config.method ?? 'GET'} ${data.config.url}`
            : data.type === 'code'
            ? 'Custom JavaScript'
            : data.type === 'filter'
            ? (data.config?.condition || 'Condition')
            : data.type === 'slack'
            ? (data.config?.webhook_url ? 'Slack webhook configured' : 'Slack — not configured')
            : data.type === 'gmail'
            ? (data.config?.to ? `To: ${data.config.to}` : 'Gmail — not configured')
            : meta.label ?? data.type
          }
        </div>

        {/* Step status dot (shown when viewing a run) */}
        {data.runStatus && (
          <div style={{
            position: 'absolute', top: 8, right: 8,
            width: 8, height: 8, borderRadius: '50%',
            background:
              data.runStatus === 'success' ? 'var(--green)' :
              data.runStatus === 'error'   ? 'var(--red)'   :
              data.runStatus === 'running' ? 'var(--cyan)'  : 'var(--text-muted)',
          }} />
        )}

        <Handle type="source" position={Position.Bottom} />
      </div>
    </>
  );
}

const NODE_TYPES = {
  triggerNode: TriggerNode,
  stepNode:    StepNode,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const GAP_Y   = 160;
const NODE_X  = 80;
const START_Y = 60;

export function buildNodes(workflow) {
  const nodes = [
    {
      id:       'trigger',
      type:     'triggerNode',
      position: { x: NODE_X, y: START_Y },
      data: {
        label:       'Trigger',
        triggerType: workflow.trigger_type ?? 'webhook',
        cronExpr:    workflow.trigger_config?.cron_expression ?? '',
      },
    },
  ];

  const steps = workflow.steps ?? [];
  steps.forEach((step, i) => {
    nodes.push({
      id:       step.id,
      type:     'stepNode',
      position: step.position ?? { x: NODE_X, y: START_Y + GAP_Y * (i + 1) },
      data: {
        stepId:    step.id,
        name:      step.name,
        type:      step.type,
        config:    step.config ?? {},
        runStatus: step.runStatus,  // only populated in run-view mode
      },
    });
  });

  return nodes;
}

export function buildEdges(workflow) {
  const steps = workflow.steps ?? [];
  return steps.map((step, i) => ({
    id:       `e-${i}`,
    source:   i === 0 ? 'trigger' : steps[i - 1].id,
    target:   step.id,
    type:     'smoothstep',
    animated: true,
  }));
}

// ── Canvas component ──────────────────────────────────────────────────────────
export default function Canvas({ workflow, selectedNodeId, onSelectNode, onDeleteNode, onPositionChange }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Sync nodes/edges when workflow changes
  useEffect(() => {
    const newNodes = buildNodes(workflow);
    const newEdges = buildEdges(workflow);

    // Inject delete handler into step node data
    const withHandler = newNodes.map(n =>
      n.type === 'stepNode'
        ? { ...n, data: { ...n.data, onDelete: onDeleteNode } }
        : n
    );

    setNodes(withHandler);
    setEdges(newEdges);
  }, [workflow, onDeleteNode]);

  const handleNodeClick = useCallback((_, node) => {
    onSelectNode?.(node.id);
  }, [onSelectNode]);

  const handlePaneClick = useCallback(() => {
    onSelectNode?.(null);
  }, [onSelectNode]);

  const handleNodesChangeWrapped = useCallback((changes) => {
    onNodesChange(changes);
    // Report position changes back to parent so they can be saved
    const posChanges = changes.filter(c => c.type === 'position' && c.dragging === false);
    if (posChanges.length > 0) onPositionChange?.(posChanges);
  }, [onNodesChange, onPositionChange]);

  // Highlight selected node
  const displayNodes = nodes.map(n => ({
    ...n,
    selected: n.id === selectedNodeId,
  }));

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={displayNodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        onNodesChange={handleNodesChangeWrapped}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        minZoom={0.3}
        maxZoom={2}
        deleteKeyCode={null}  // we handle deletion ourselves
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1.5} />
        <Controls />
        <MiniMap
          style={{ background: 'var(--surface)' }}
          nodeColor={n => n.type === 'triggerNode' ? 'var(--primary)' : 'var(--surface-3)'}
        />
      </ReactFlow>
    </div>
  );
}
